import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput,
  CommandItem, CommandList, CommandSeparator,
} from "@/components/ui/command";
import { api } from "@/lib/api";
import {
  MagnifyingGlass, House, UsersThree, UserPlus, CalendarBlank,
  ChartLine, Envelope, ClockCounterClockwise, Gear, Archive, Trash,
} from "@phosphor-icons/react";

// Global command palette — opens on Cmd+K / Ctrl+K anywhere in the app.
// Two sections:
//   1. Clients — live search against /api/clients/search (debounced)
//   2. Quick Actions — jump to any of the app's pages
const QUICK_ACTIONS = [
  { label: "Go to Dashboard",    path: "/dashboard", icon: House },
  { label: "View Clients",       path: "/view",      icon: UsersThree },
  { label: "Add Client",         path: "/add",       icon: UserPlus },
  { label: "Upcoming Tasks",     path: "/tasks",     icon: CalendarBlank },
  { label: "Reports",            path: "/reports",   icon: ChartLine },
  { label: "Email History",      path: "/emails",    icon: Envelope },
  { label: "Activity Log",       path: "/activity",  icon: ClockCounterClockwise },
  { label: "Settings",           path: "/settings",  icon: Gear },
  { label: "Archived Clients",   path: "/archive",   icon: Archive },
  { label: "Trash",              path: "/trash",     icon: Trash },
];

const CommandPalette = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  // Debounced client search — 200ms after last keystroke.
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (!q) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/clients/search", { params: { q } });
        // API returns a bare list; support both shapes for safety.
        setResults(Array.isArray(data) ? data : (data.items || []));
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(debounceRef.current);
  }, [query, open]);

  // Reset on close so re-opens start clean.
  useEffect(() => {
    if (!open) {
      setQuery("");
      setResults([]);
    }
  }, [open]);

  const go = (path) => {
    onOpenChange(false);
    navigate(path);
  };

  const filteredActions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return QUICK_ACTIONS;
    return QUICK_ACTIONS.filter((a) => a.label.toLowerCase().includes(q));
  }, [query]);

  return (
    <CommandDialog
      open={open}
      onOpenChange={onOpenChange}
      shouldFilter={false}
      data-testid="command-palette"
    >
      <CommandInput
        placeholder="Search clients or jump to a page…"
        value={query}
        onValueChange={setQuery}
        data-testid="cmdk-input"
      />
      <CommandList data-testid="cmdk-list">
        {!query.trim() && results.length === 0 && (
          <CommandEmpty>Start typing to search clients or pick a shortcut below.</CommandEmpty>
        )}
        {query.trim() && !loading && results.length === 0 && filteredActions.length === 0 && (
          <CommandEmpty>No matches for "{query}".</CommandEmpty>
        )}

        {results.length > 0 && (
          <CommandGroup heading="Clients">
            {results.map((c) => (
              <CommandItem
                key={c.id}
                value={`${c.first_name} ${c.last_name} ${c.email || ""}`}
                onSelect={() => go(`/clients/${c.id}`)}
                data-testid={`cmdk-client-${c.id}`}
              >
                <MagnifyingGlass size={14} weight="duotone" className="mr-2 shrink-0" />
                <span className="font-medium">{c.first_name} {c.last_name}</span>
                {c.email && <span className="ml-2 text-xs text-muted-foreground">{c.email}</span>}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {results.length > 0 && filteredActions.length > 0 && <CommandSeparator />}

        {filteredActions.length > 0 && (
          <CommandGroup heading="Quick actions">
            {filteredActions.map((a) => {
              const Icon = a.icon;
              return (
                <CommandItem
                  key={a.path}
                  value={a.label}
                  onSelect={() => go(a.path)}
                  data-testid={`cmdk-action-${a.path.slice(1)}`}
                >
                  <Icon size={14} weight="duotone" className="mr-2 shrink-0" />
                  {a.label}
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
};

export default CommandPalette;
